import { fetchStockPrice, hasApiKey } from './yahooFinance';
import { getCurrentDate, isSameDate } from '../utils/helpers';
import toast from 'react-hot-toast';

/**
 * Check if fund prices need to be refreshed (not updated today)
 * @param {Array} fundPrices - Array of fund price objects
 * @returns {boolean} True if prices need refresh
 */
export const needsPriceRefresh = (fundPrices) => {
  if (!fundPrices || fundPrices.length === 0) {
    return false;
  }

  const today = getCurrentDate();
  const fundsWithSymbols = fundPrices.filter(fund => fund.symbol && fund.symbol.trim() !== '');

  if (fundsWithSymbols.length === 0) {
    return false;
  }

  // Check if any fund hasn't been updated today
  return fundsWithSymbols.some(fund => {
    if (!fund.lastUpdated) return true; // Never updated
    return !isSameDate(fund.lastUpdated, today);
  });
};

/**
 * Refresh all fund prices from API
 * @param {Array} fundPrices - Array of fund price objects
 * @param {Function} updateFundPrice - Function to update a fund price
 * @returns {Promise<Object>} Result with success/error counts
 */
export const refreshAllFundPrices = async (fundPrices, updateFundPrice) => {
  if (!hasApiKey()) {
    console.log('No RapidAPI key configured, skipping price refresh');
    return { success: 0, errors: 0, skipped: true };
  }

  const fundsWithSymbols = fundPrices.filter(fund => fund.symbol && fund.symbol.trim() !== '');

  if (fundsWithSymbols.length === 0) {
    return { success: 0, errors: 0, skipped: true };
  }

  let successCount = 0;
  let errorCount = 0;
  const errors = [];

  for (const fund of fundsWithSymbols) {
    try {
      const priceData = await fetchStockPrice(fund.symbol);
      await updateFundPrice(fund.id, {
        price: priceData.price,
        currency: priceData.currency,
      }, true); // Pass silent = true to suppress individual toasts
      successCount++;
    } catch (error) {
      console.error(`Failed to refresh price for ${fund.symbol}:`, error);
      errorCount++;
      errors.push({ symbol: fund.symbol, error: error.message });
    }
  }

  return { success: successCount, errors: errorCount, errorDetails: errors };
};

/**
 * Create daily snapshots for all portfolios
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Function} addSnapshot - Function to add a snapshot
 * @returns {number} Number of snapshots created
 */
export const createDailySnapshots = async (portfolios, addSnapshot) => {
  if (!portfolios || portfolios.length === 0) {
    return 0;
  }

  const today = getCurrentDate();
  let snapshotsCreated = 0;

  for (const portfolio of portfolios) {
    try {
      const snapshot = {
        portfolioId: portfolio.id,
        date: today,
        value: portfolio.currentValue || 0,
        costBasis: portfolio.costBasis || 0,
      };

      await addSnapshot(snapshot);
      snapshotsCreated++;
    } catch (error) {
      console.error(`Failed to create snapshot for portfolio ${portfolio.id}:`, error);
    }
  }

  return snapshotsCreated;
};

/**
 * Check if we already have a snapshot for today for a given portfolio
 * @param {Array} snapshots - Array of snapshot objects
 * @param {string} portfolioId - Portfolio ID
 * @returns {boolean} True if snapshot exists for today
 */
export const hasSnapshotToday = (snapshots, portfolioId) => {
  const today = getCurrentDate();
  return snapshots.some(snap =>
    snap.portfolioId === portfolioId && isSameDate(snap.date, today)
  );
};

/**
 * Check if we need to create snapshots for any portfolio
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Array} snapshots - Array of snapshot objects
 * @returns {boolean} True if any portfolio needs a snapshot
 */
export const needsDailySnapshots = (portfolios, snapshots) => {
  if (!portfolios || portfolios.length === 0) {
    return false;
  }

  return portfolios.some(portfolio => !hasSnapshotToday(snapshots, portfolio.id));
};

/**
 * Main function: Refresh prices and create snapshots if needed
 * @param {Object} params - Object containing all needed data and functions
 * @returns {Promise<Object>} Result summary
 */
export const refreshPricesAndCreateSnapshots = async ({
  fundPrices,
  portfolios,
  snapshots,
  updateFundPrice,
  addSnapshot,
  silent = false,
  skipSnapshots = false,
  forceRefresh = false,
}) => {
  const results = {
    pricesRefreshed: false,
    priceResults: null,
    snapshotsCreated: false,
    snapshotCount: 0,
  };

  // Step 1: Check if prices need refresh (or force refresh)
  if (forceRefresh || needsPriceRefresh(fundPrices)) {
    const fundsToUpdate = fundPrices.filter(fund => fund.symbol && fund.symbol.trim() !== '');

    // Show starting notification
    if (!silent && fundsToUpdate.length > 0) {
      toast.loading(`Updating ${fundsToUpdate.length} fund price${fundsToUpdate.length !== 1 ? 's' : ''}...`, { id: 'fund-price-refresh' });
    }

    const priceResults = await refreshAllFundPrices(fundPrices, updateFundPrice);
    results.pricesRefreshed = true;
    results.priceResults = priceResults;

    // Show completion notification (dismiss the loading toast)
    if (!silent && !priceResults.skipped) {
      toast.dismiss('fund-price-refresh');

      if (priceResults.success > 0 && priceResults.errors === 0) {
        // All succeeded
        toast.success(`Updated ${priceResults.success} fund price${priceResults.success !== 1 ? 's' : ''}`);
      } else if (priceResults.success > 0 && priceResults.errors > 0) {
        // Some succeeded, some failed
        toast.success(`Updated ${priceResults.success} of ${priceResults.success + priceResults.errors} fund prices`);
      } else if (priceResults.errors > 0) {
        // All failed
        toast.error(`Failed to update ${priceResults.errors} fund price${priceResults.errors !== 1 ? 's' : ''}`);
      }
    }
  }

  // Step 2: Check if snapshots need to be created (unless skipSnapshots is true)
  if (!skipSnapshots && (forceRefresh || needsDailySnapshots(portfolios, snapshots))) {
    if (!silent) {
      console.log(forceRefresh ? 'Force creating snapshots...' : 'Creating daily snapshots...');
    }

    const snapshotCount = await createDailySnapshots(portfolios, addSnapshot);
    results.snapshotsCreated = true;
    results.snapshotCount = snapshotCount;

    if (!silent && snapshotCount > 0) {
      console.log(`Created ${snapshotCount} daily snapshot${snapshotCount !== 1 ? 's' : ''}`);
    }
  }

  return results;
};
